import { Outlet, Navigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthStore } from "@/stores/auth-store";
import { BottomDock } from "./sidebar";
import { TopBar } from "./top-bar";
import { MobileTabBar } from "./mobile-tab-bar";
import { MobileHeader } from "./mobile-header";
import { CommandPalette } from "@/components/common/command-palette";
import { ToastContainer } from "@/components/toast-container";
import { ErrorBoundary } from "@/components/errors/ErrorBoundary";
import { ChatProvider } from "@/providers/ChatProvider";
import { useChatStore } from "@/stores/chat-store";
import { SpotlightProvider } from "@/providers/SpotlightProvider";
import { SpotlightOverlay } from "@/components/chat/SpotlightOverlay";
import { ArrowUp } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useOnboarding } from "@/hooks/use-onboarding";
import { HelpCenterModal } from "@/components/onboarding/help-center-modal";
import { ToolboxFrame } from "@/components/grooming/desktop-shell/toolbox-frame";
import { GroomingCommandPalette } from "@/components/grooming/command-palette";
import { GroomingToasts } from "@/components/grooming/grooming-toasts";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { rememberReturnTo } from "@/lib/auth/return-to";

/**
 * Pages SpecTree Toolbox shows in its own chrome (title bar and icon rail) instead of SpecTree's:
 * Grooming and every `/toolbox` page, including the Formulator at `/toolbox/formulator` (E282).
 */
export function usesToolboxChrome(pathname: string): boolean {
  return /^\/(grooming|toolbox)(\/|$)/.test(pathname);
}

/** Grooming pages use the grooming command palette and toasts in a browser too. */
function isGroomingPath(pathname: string): boolean {
  return /^\/grooming(\/|$)/.test(pathname);
}

export function AppLayout() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const dockMode = useChatStore((s) => s.dockMode);
  const panelState = useChatStore((s) => s.panelState);
  const helpCenterDockMode = useChatStore((s) => s.helpCenterDockMode);
  const isHelpCenterOpen = useChatStore((s) => s.isHelpCenterOpen);
  const openHelpCenter = useChatStore((s) => s.openHelpCenter);
  const isMobile = useIsMobile();
  const { isNewUser } = useOnboarding();
  const toolboxBridge = useToolboxBridge();
  const isRightPanelOpen = !isMobile && dockMode === "right" && panelState === "open";
  const isHelpCenterDocked = !isMobile && helpCenterDockMode === "right" && isHelpCenterOpen;

  useEffect(() => {
    const onScroll = () => { setShowScrollTop(window.scrollY > 300); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); };
  }, []);

  // Reset on page change and scroll to top
  useEffect(() => {
    setShowScrollTop(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    // Inside SpecTree Toolbox the app opens its own pages; never pop the
    // SpecTree Help Center over them (PI-SPEC-SW-1407).
    if (!isNewUser || toolboxBridge) return;

    const timer = setTimeout(() => {
      if (hasAutoOpenedRef.current) return;
      hasAutoOpenedRef.current = true;
      if (!useChatStore.getState().isHelpCenterOpen) {
        openHelpCenter("menu");
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [isNewUser, openHelpCenter, toolboxBridge]);

  const scrollToTop = useCallback(() => {
    const start = window.scrollY;
    const duration = 800;
    const startTime = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, start * (1 - ease(progress)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Come back here after sign-in (PI-SPEC-SW-1407): a shared agenda link or
    // the Toolbox home, not always the dashboard.
    rememberReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  // Inside SpecTree Toolbox, grooming and toolbox pages render in the app's
  // own chrome (epic PI-SPEC-SW-E280): no SpecTree sidebar, top bar or dock,
  // and no SpecTree Help Center, which covers SpecTree rather than the Toolbox.
  if (toolboxBridge && usesToolboxChrome(location.pathname)) {
    return (
      <ChatProvider>
        <SpotlightProvider>
          <ToolboxFrame>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </ToolboxFrame>
          <ToastContainer />
        </SpotlightProvider>
      </ChatProvider>
    );
  }

  const onGroomingPage = isGroomingPath(location.pathname);

  return (
    <ChatProvider>
      <SpotlightProvider>
        <div className="flex min-h-screen flex-col bg-background pb-safe">
          {/* Main content area — TopBar scrolls with content */}
          <main className={cn(
            "flex-1 transition-[margin] duration-300 ease-out",
            isMobile ? "px-4 pb-28 pt-[calc(env(safe-area-inset-top,0px)+3rem)]" : "px-16 pb-24",
            (isRightPanelOpen || isHelpCenterDocked) && "mr-[386px] pr-4"
          )}>
            {!isMobile && <TopBar />}
            {isMobile && <MobileHeader />}
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>

          {/* Scroll to top button — floats above the dock */}
          <AnimatePresence>
            {showScrollTop && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                onClick={scrollToTop}
                className={cn(
                  "fixed z-[51] h-10 w-10 rounded-full glass-card border border-white/[0.12] flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/[0.08] transition-colors shadow-lg",
                  isMobile ? "bottom-36 left-1/2 -translate-x-1/2" : "bottom-24 left-1/2 -translate-x-1/2"
                )}
              >
                <ArrowUp className="h-4 w-4" />
              </motion.button>
            )}
          </AnimatePresence>

          {/* Spotlight overlay — renders above content, below chat panel */}
          <SpotlightOverlay />

          {/* Unified bottom dock + chat container (desktop only) */}
          {!isMobile && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex flex-col items-stretch w-fit">
            <BottomDock />
          </div>
          )}

          {/* Mobile-only: bottom tab bar with chat pill — floating like desktop */}
          {isMobile && (
            <div className="fixed bottom-3 left-3 right-3 z-50 flex flex-col items-stretch pb-safe pointer-events-none">
              {/* Black gradient fade behind nav — extends from above nav to bottom of screen */}
              <div className="absolute -top-10 -left-3 -right-3 -bottom-3 bg-gradient-to-b from-transparent via-black/70 to-black pointer-events-none" />
              {/* Nav bar */}
              <div className="relative glass-card border border-white/[0.08] rounded-2xl shadow-2xl pointer-events-auto">
                <MobileTabBar />
              </div>
            </div>
          )}

          <HelpCenterModal />
          {onGroomingPage ? <GroomingCommandPalette /> : <CommandPalette />}
          <ToastContainer />
          {onGroomingPage && <GroomingToasts />}
        </div>
      </SpotlightProvider>
    </ChatProvider>
  );
}
